import { FC, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"

import {
  Backdrop,
  Box,
  Button,
  Grid,
  IconButton,
} from "../../../components/mui.components"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { fetchFileList } from "../../../core/app/slices/clinical/clinicalThunkApi"
import { fetchFilesList } from "../../../core/app/slices/records/photograph/photographThunkApi"
import CloseIcon from "@/assets/svgs/close.svg"
import { RootState } from "../../../core/app/store"
import PhotoList from "../../Uassist/PhotoList"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import {
  resetPrescriptionSchema,
  resetSchema,
  setIsRecordsUpdated,
} from "@/core/app/slices/uassist/formSlice"
import OnholdMessage from "./components/OnholdMessage"
import { Form, FormContentRef } from "@/components/Form"
import { FormRenderMode, FormType } from "@/components/Form/types"
import { useTranslation } from "react-i18next"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { UButton } from "@/ui-component"

const RxForm: FC<{
  postSettingTozips: () => void
  handleBack?: () => void
  postMessageUpdate?: () => void
}> = ({ postSettingTozips, handleBack, postMessageUpdate }) => {
  const dispatch = useAppDispatch()
  const [upperArchType, setUpperArchType] = useState<number>(null)
  const [lowerArchType, setLowerArchType] = useState<number>(null)
  const [selectedPreview, setSelectedPreview] = useState("")
  const [isRetainer, setIsRetainer] = useState<boolean>(false)

  const { fileList } = useAppSelector((state: RootState) => state.PhotosService)
  const { onHoldMsg, isRecordUpdated  } = useAppSelector(
    (state: RootState) => state.formService,
  )

  const formRef = useRef<FormContentRef>(null)

  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { patientId, caseId } = useParams()
  const [isRetainerForm, setIsRetainerForm] = useState(false)
  const [isFormSummary, setIsFormSummary] = useState(false)
  const [currentFormType, setCurrentFormType] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const { t } = useTranslation("common")
  useEffect(() => {
    dispatch(fetchFileList({ patientId, caseId }))
    if (!fileList.length) dispatch(fetchFilesList({ patientId, caseId }))
    return () => {
      dispatch(resetPrescriptionSchema())
      dispatch(resetSchema())
    }
  }, [dispatch])

  useEffect(() => {
    if (caseDetail) {
      const udesignCategory = caseDetail?.udesign_category
      if (udesignCategory) {
        const arches = udesignCategory.split("/")
        setIsRetainerForm(arches?.includes("R") && !arches?.includes("A"))
      }
    }
    if (
      caseDetail &&
      caseDetail.udesign_category &&
      !caseDetail?.udesign_category?.includes("A")
    ) {
      setIsRetainer(true)
    }
  }, [caseDetail])

  const handleFormSaved = (val: boolean) => {
    setIsFormSummary(val)
  }

  const formDisplayDetails = useMemo(()=>{
    return (formName: string) => {
      let details = {
        formTitle: null,
        formDescription: null,
      }
      
      if(!formName) return details 

      if( isFormSummary ){
        // form in summary mode
        details.formDescription =  t("uassist.summary.summarydesc") 
        
        switch(formName.toLowerCase()){
          case FORM_CONSTANT.PRESCRIPTION.toLowerCase():
            details.formTitle = t("uassist.uDC.prescriptionSummary")
            break
          case FORM_CONSTANT.PRESCRIPTION_2.toLowerCase():
            details.formTitle = t("uassist.selfPlanned.prescriptionSummary")
            break
          case FORM_CONSTANT.RETAINER.toLowerCase():
            details.formTitle = t("uassist.uDC.retainerSummary")
            break
          case FORM_CONSTANT.REFINEMENT.toLowerCase():
            details.formTitle = t("uassist.uDC.refinementSummary")
            break
          case FORM_CONSTANT.REFINEMENT_2.toLowerCase():
            details.formTitle = t("uassist.selfPlanned.refinementSummary")
            break
        }
      }else{
        // form in edit mode
        
        switch(formName.toLowerCase()){
          case FORM_CONSTANT.PRESCRIPTION.toLowerCase():
            details.formTitle = t("uassist.uDC.prescription")
            break
          case FORM_CONSTANT.PRESCRIPTION_2.toLowerCase():
            details.formTitle = t("uassist.selfPlanned.prescription")
            break
          case FORM_CONSTANT.RETAINER.toLowerCase():
            details.formTitle = t("uassist.uDC.retainer")
            break
          case FORM_CONSTANT.REFINEMENT.toLowerCase():
            details.formTitle = t("uassist.uDC.refinement")
            break
          case FORM_CONSTANT.REFINEMENT_2.toLowerCase():
            details.formTitle = t("uassist.selfPlanned.refinement")
            break
        }
      }
  
      return details
    }

  },[isFormSummary, caseDetail, isRetainer, t])

  return (
    <>
      <Grid
        container
        sx={{ display: "flex", justifyContent: "space-between" }}
        id="on-holdform"
      >
        <Grid item>
          <PhotoList
            setSelectedPreview={setSelectedPreview}
            upperArchType={upperArchType}
            lowerArchType={lowerArchType}
          />
          <OnholdMessage />
        </Grid>
        <Grid item sx={{
          width: 'fit-content',
          minWidth: '560px',
          maxWidth: '620px',
        }}>
          {caseDetail.id && (
            <Form
              ref={formRef}
              details={{
                patientId,
                caseId,
                formType: FormType.UASSIST,
                uassistRound: caseDetail?.uassist_round || 0,
                fetchSavedData: true,
                displayMeta: formDisplayDetails,
              }}
              mode={
                isFormSummary ? FormRenderMode.SUMMARY : FormRenderMode.EDIT
              }
              onChange={(data) => {
                dispatch(setIsRecordsUpdated(true))
                if (
                  setUpperArchType &&
                  data.arches_to_treat?.upper &&
                  data.arches_to_treat.upper.aligner_type
                ) {
                  setUpperArchType(
                    data.arches_to_treat.upper.aligner_type === "Aligner"
                      ? 0
                      : 1,
                  )
                }
                if (
                  setLowerArchType &&
                  data.arches_to_treat?.lower &&
                  data.arches_to_treat.lower.aligner_type
                ) {
                  setLowerArchType(
                    data.arches_to_treat.lower.aligner_type === "Aligner"
                      ? 0
                      : 1,
                  )
                }
                console.log("Form data changed:", data)
              }}
              showValidationErrors={false}
              onFormSaved={handleFormSaved}
              setCurrentFormType={setCurrentFormType}
              onSavingChange={setIsSaving}
            />
          )}
          {currentFormType === FormType.UASSIST && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
              }}
              mt={2}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  if (!isFormSummary) {
                    handleBack && handleBack()
                  } else {
                    setIsFormSummary(false)
                  }
                }}
                sx={{ width: "92px", height: "36px" }}
                disabled={!isFormSummary && isSaving}
                startIcon={<ChevronLeftIcon fontSize={"small"} sx={{ marginRight: "3px" }} />}
              >
                {t("button.back")}
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {!isFormSummary ? (
                <Button
                  onClick={async () => {
                    try {
                      formRef.current?.setShowValidationErrors(true)
                      if (formRef.current.showValidationErrors) {
                        formRef?.current?.validateErrors()
                      }
                      console.log("Form data updated successfully")
                    } catch (error) {
                      console.error("Error updating form data:", error)
                    }
                  }}
                  variant="contained"
                  sx={{ width: "146px", height: "36px" }}
                  disabled={!isRecordUpdated && !onHoldMsg?.length || isSaving}
                >
                  {t("button.review")}
                  <ChevronRightIcon
                    fontSize={"small"}
                    sx={{
                      height: "20px",
                      width: "20px",
                      marginLeft: "8px",
                    }}
                  />
                </Button>
              ) : (
                <UButton
                  onClickHandler={async () => {
                    formRef.current?.submitToUassist()
                  }}
                  variant="contained"
                  sxProp={{ width: "196px", height: "36px" }}
                  oneTimeClick={true}
                  btnText={t("button.submittoUassist")}
                  endIcon={
                    <ChevronRightIcon
                     sx={{ marginLeft: "3px" }}
                      fontSize={"small"}
                    />
                  }
                />
              )}
            </Box>
          )}
        </Grid>
      </Grid>
      {selectedPreview && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <Box
            display="flex"
            justifyContent="center"
            position="fixed"
            top="120px"
            bottom="120px"
            left="120px"
            right="120px"
            overflow="hidden"
          >
            <Box
              id="backdrop"
              position="relative"
              width="inherit"
              height="inherit"
            >
              <img
                src={selectedPreview}
                alt={"fullscreenimg"}
                style={{
                  width: "100%",
                  height: "100%",
                }}
                loading="lazy"
              />
              <Box
                component={"div"}
                id="closeicon"
                position="absolute"
                top="0px"
                right="0px"
                sx={{ padding: "8px" }}
              >
                <IconButton
                  size="medium"
                  sx={{
                    fontSize: "large",
                    backgroundColor: "divider",
                    padding: "0px",
                    ":hover": { backgroundColor: "divider" },
                  }}
                  onClick={() => setSelectedPreview("")}
                >
                  <Box
                    component={"img"}
                    src={CloseIcon}
                    alt={"Logo"}
                    loading="lazy"
                    sx={{ cursor: "pointer", padding: "12px" }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Backdrop>
      )}
    </>
  )
}

export default RxForm
